import type {EnvironmentResolver} from "@common-core/web-component-runtime/runtime";
import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime/RuntimeEnvironment";
import {consume, provide} from "@lit/context";
import {Task} from "@lit/task/task.js";
import {
  initialize as initLD,
  type LDClient,
  type LDContext
} from "launchdarkly-js-client-sdk";
import {LitElement} from "lit";
import {customElement, property} from "lit/decorators.js";
import {html, unsafeStatic} from "lit/static-html.js";

import type {BridgeBarDataContextValue} from "../backend/BridgeBarData";
import {BridgeBarDataContext} from "../backend/BridgeBarDataContext";
import type {BridgeBarConfig} from "../config";
import {BridgeBarConfigContext} from "../config";
import {ROOT_TAG} from "../globals";
import {RuntimeContext, type Environment} from "../runtime";
import {environments} from "../runtime/environments";
import {LDClientContext} from "./LaunchDarklyContext";
import type {LDClientContextValue} from "./LaunchDarklyContext";
import {TaskStatus} from "@lit/task";

const tagName = `${ROOT_TAG}-launchdarkly-provider`;
export const LaunchDarklyProvider = unsafeStatic(tagName);

const LD_INITIALIZATION_TIMEOUT_SECONDS = 5;

@customElement(tagName)
export class LaunchDarklyProviderElement extends LitElement {
  @consume({context: RuntimeContext, subscribe: true})
  runtimeEnvironment: RuntimeEnvironment<Environment, unknown>;

  @consume({context: BridgeBarDataContext, subscribe: true})
  bridgeBarData: BridgeBarDataContextValue;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private bridgeBarConfig: BridgeBarConfig | undefined;

  @property({type: LDClientContext})
  @provide({context: LDClientContext})
  ldContextValue: LDClientContextValue = {
    ldClient: undefined,
    ldContext: undefined
  };

  /** Memoizes the in-flight init so concurrent Task runs cannot create two clients. */
  private ldClientInit: Promise<LDClient> | undefined;

  /** Serialized context of the last `identify()`, to skip redundant re-identifies. */
  private lastIdentifiedContext: string | undefined;

  private readonly ldClientProvider = new Task<
    readonly [
      RuntimeEnvironment<Environment, unknown>,
      BridgeBarDataContextValue,
      BridgeBarConfig | undefined
    ]
  >(this, {
    args: () =>
      [
        this.runtimeEnvironment,
        this.bridgeBarData,
        this.bridgeBarConfig
      ] as const,

    task: async ([runtimeEnvironment, bridgeBarData]) => {
      if (!runtimeEnvironment) {
        return;
      }

      const ownsClient = !this.ldContextValue.ldClient;
      const ldClient = await this.ensureLDClient(bridgeBarData);
      let identified = false;

      if (bridgeBarData?.status === "complete") {
        identified = await this.identifyLDContext(ldClient, bridgeBarData);
      }

      if (ownsClient || identified) {
        this.publishLDContext(ldClient);
      }
    }
  });

  private async identifyLDContext(
    ldClient: LDClient,
    bridgeBarData: BridgeBarDataContextValue
  ): Promise<boolean> {
    const context = this.buildLDContext(bridgeBarData);
    const serialized = JSON.stringify(context);
    if (serialized === this.lastIdentifiedContext) {
      return false;
    }

    try {
      await ldClient.identify(context);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[LaunchDarklyProviderElement] Failed to identify the LaunchDarkly context:",
        error
      );
      return false;
    }

    this.lastIdentifiedContext = serialized;
    return true;
  }

  private get bridgeBarEnvironment() {
    const {runtime} = this.runtimeEnvironment;
    return (environments[runtime.id] as EnvironmentResolver<unknown, unknown>)(
      runtime
    ) as Environment;
  }

  /** Resolves the single LD client; one supplied from outside is used as-is. */
  private ensureLDClient(
    bridgeBarData: BridgeBarDataContextValue
  ): LDClient | Promise<LDClient> {
    const provided = this.ldContextValue.ldClient;
    if (provided) {
      return provided;
    }
    this.ldClientInit ??= this.initLDClient(bridgeBarData);
    return this.ldClientInit;
  }

  private async initLDClient(
    bridgeBarData: BridgeBarDataContextValue
  ): Promise<LDClient> {
    const context = this.buildLDContext(bridgeBarData);
    const client = initLD(this.bridgeBarEnvironment.ldClientSideId, context);
    try {
      await client.waitForInitialization(LD_INITIALIZATION_TIMEOUT_SECONDS);
      this.lastIdentifiedContext = JSON.stringify(context);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[LaunchDarklyProviderElement] Failed to initialize LD client:",
        error
      );
    }
    return client;
  }

  /**
   * Publishes the client and its context as a matched pair read from that same
   * client, so consumers cannot gate on one client's context while evaluating
   * against another. Always a new object for `@lit/context` change detection.
   */
  private publishLDContext(ldClient: LDClient): void {
    this.ldContextValue = {ldClient, ldContext: this._readContext(ldClient)};
  }

  private _readContext(ldClient: LDClient): LDContext | undefined {
    try {
      return ldClient.getContext();
    } catch {
      return undefined;
    }
  }

  private buildLDContext(bridgeBarData: BridgeBarDataContextValue): LDContext {
    const partition = this.runtimeEnvironment.runtime.partition.includes(
      "non-prod"
    )
      ? "NON_PROD"
      : "PROD";

    const userData =
      bridgeBarData?.status === "complete" ? bridgeBarData.data : null;
    return {
      anonymous: !userData,
      kind: "user",
      id: userData?.id,
      key: userData?.id ?? this.bridgeBarEnvironment.ldClientSideId,
      username: userData?.username,
      partition,
      applicationId: this.bridgeBarConfig?.applicationId?.toUpperCase(),
      currentApplication: this.bridgeBarConfig?.applicationId?.toUpperCase(),
      deployment: this.bridgeBarConfig?.deployment
    };
  }

  render() {
    if (this.ldClientProvider.status === TaskStatus.ERROR) {
      console.error(
        "[LaunchDarklyProviderElement] Failed to initialize LD client:",
        this.ldClientProvider.error
      );
    }
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: LaunchDarklyProviderElement;
  }
}
